import { Button, Chip, Input, Typography } from "@material-ui/core";
import { useMemo, useState } from "react";
import { MUTATIONS } from "../../../graphql";
import { useServiceReservations } from "../../../graphql/hooks";
import {
  ComposableServiceConfigPaymentMode,
  ServiceReservation,
  ServiceReservationStatus as ServiceReservationStatusValue,
} from "../../../graphql/models";
import { useSnackbarMessage, useSnackbarMutation } from "../../../hooks";

export interface ServiceReservationStatusProps {
  status: ServiceReservationStatusValue;
  serviceReservation: ServiceReservation;
  onStatusUpdated?: () => void;
  sendPushOnStatusChange?: boolean;
  enableStatusChange?: boolean;
}

export function ServiceReservationRender(
  status: ServiceReservationStatusValue,
  deletedAt: number | undefined
) {
  const result = {
    color: "",
    background: "",
    displayText: "",
  };

  switch (status) {
    case ServiceReservationStatusValue.PAYMENT_PENDING:
    case ServiceReservationStatusValue.PENDING:
    case ServiceReservationStatusValue.REQUESTED_PAYMENT:
      result.color = "black";
      result.background = "darkorange";
      result.displayText = "Attesa pagamento";
      break;
    case ServiceReservationStatusValue.WAITING_FOR_PROVIDER_CONFIRM:
      result.color = "black";
      result.background = "gold";
      result.displayText = "Attesa conferma";
      break;
    case ServiceReservationStatusValue.WAITING_FOR_CAPTURE:
      result.color = "black";
      result.background = "lightgreen";
      result.displayText = "Conferma pre autorizzazione";
      break;
    default:
      result.color = "white";
      result.background = "darkgreen";
      result.displayText = "Pagato";
      break;
  }

  if (deletedAt) {
    result.color = "white";
    result.background = "darkred";
    result.displayText = `Cancellata il ${new Date(
      deletedAt
    ).toLocaleDateString()}`;
  }

  return result;
}

export function ServiceReservationStatus(props: ServiceReservationStatusProps) {
  const {
    status,
    serviceReservation,
    onStatusUpdated,
    sendPushOnStatusChange = false,
    enableStatusChange = true,
  } = props;

  const isDeleted = Boolean(serviceReservation.deletedBy);
  const [amountToCapture, setAmountToCapture] = useState<number>(
    serviceReservation.totalPrice
  );

  const serviceReservationId = useMemo(
    () => serviceReservation.id,
    [serviceReservation]
  );
  const serviceReservationPaymentMode = useMemo(
    () => serviceReservation.service.paymentMode,
    [serviceReservation]
  );

  const { raiseSuccessSnackbar } = useSnackbarMessage();

  const [
    invokeCaptureReservationPaymentIntent,
    { loading: captureReservationPaymentIntentLoading },
  ] = useSnackbarMutation(MUTATIONS.CAPTURE_RESERVATION_PAYMENT_INTENT, {
    variables: {
      id: serviceReservationId,
      amountToCapture,
    },
    onCompleted: () => {
      raiseSuccessSnackbar("Pagamento terminato con successo!");
      onStatusUpdated?.();
    },
  });

  const [
    invokeUpdateManagedReservation,
    { loading: updateManagedReservationLoading },
  ] = useSnackbarMutation(MUTATIONS.UPDATE_MANAGED_RESERVATION, {
    variables: {
      id: serviceReservationId,
      newAmount: serviceReservation.totalPrice,
      lastTimeForPayment: serviceReservation.lastTimeForPayment,
      sendPushNotification: sendPushOnStatusChange,
      pushMessage: serviceReservation.pushMessage,
    },
    onCompleted: () => {
      raiseSuccessSnackbar(`Informazioni aggiornate!`);
      onStatusUpdated?.();
    },
  });

  const { confirmServiceReservation, confirmServiceReservationLoading } =
    useServiceReservations({
      onServiceReservationConfirmed() {
        raiseSuccessSnackbar("La prenotazione è stata confermata!");
        onStatusUpdated?.();
      },
    });

  const loading = useMemo(
    () =>
      [
        captureReservationPaymentIntentLoading,
        confirmServiceReservationLoading,
        updateManagedReservationLoading,
      ].some((v) => v === true),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [captureReservationPaymentIntentLoading, confirmServiceReservationLoading]
  );

  const {
    color,
    background: backgroundColor,
    displayText: label,
  } = useMemo(
    () => {
      return ServiceReservationRender(status, serviceReservation.deletionDate);
    }, //ServiceReservationRender(status, isDeleted),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [status, isDeleted]
  );
  /*
  const backgroundColor = useMemo(() => {
    switch (status) {
      case ServiceReservationStatusValue.PENDING:
      case ServiceReservationStatusValue.WAITING_FOR_PROVIDER_CONFIRM:
        return "red";
      case ServiceReservationStatusValue.WAITING_FOR_CAPTURE:
      case ServiceReservationStatusValue.PAYMENT_PENDING:
      case ServiceReservationStatusValue.REQUESTED_PAYMENT:
        return "orange";
      case ServiceReservationStatusValue.CONFIRMED:
        return "darkgreen";
      default:
        return "red";
    }
  }, [status]);

  const label = useMemo(() => {
    if (loading) {
      return <CircularProgress />;
    } else {
      switch (status) {
        case ServiceReservationStatusValue.PENDING:
          return "In attesa di richiesta pagamento";
        case ServiceReservationStatusValue.WAITING_FOR_PROVIDER_CONFIRM:
          if (isDeleted) {
            return "Non confermata ed eliminata.";
          }
          return "In attesa di conferma, clicca per confermare";
        case ServiceReservationStatusValue.REQUESTED_PAYMENT:
          return "In attesa del pagamento";
        case ServiceReservationStatusValue.WAITING_FOR_CAPTURE:
          return "Importo pre-addebitato, clicca per addebitare.";
        case ServiceReservationStatusValue.CONFIRMED:
          return "Pagato";
      }
    }
  }, [isDeleted, loading, status]);*/

  const askCapturedAmount = useMemo(
    () =>
      status === ServiceReservationStatusValue.WAITING_FOR_CAPTURE &&
      serviceReservationPaymentMode ===
        ComposableServiceConfigPaymentMode.PRE_AUTHORIZE &&
      serviceReservationId &&
      invokeCaptureReservationPaymentIntent,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [invokeCaptureReservationPaymentIntent, serviceReservationId, status]
  );

  const changeStatusButton = useMemo(() => {
    if (enableStatusChange === false) return <></>;
    // pre authorization
    if (
      status === ServiceReservationStatusValue.WAITING_FOR_CAPTURE &&
      serviceReservationId &&
      invokeCaptureReservationPaymentIntent
    ) {
      return (
        <Button
          variant="outlined"
          onClick={() => invokeCaptureReservationPaymentIntent()}
        >
          Incassa pre autorizzazione
        </Button>
      );
    }
    // post payment
    if (
      enableStatusChange &&
      serviceReservationPaymentMode ===
        ComposableServiceConfigPaymentMode.PAY_AFTER &&
      serviceReservationId
    ) {
      return (
        <Button
          variant="outlined"
          onClick={() => invokeUpdateManagedReservation()}
        >
          Aggiorna dati {sendPushOnStatusChange ? ` e richiedi pagamento` : ``}
        </Button>
      );
    }
    // attesa di conferma dal provider
    if (
      status === ServiceReservationStatusValue.WAITING_FOR_PROVIDER_CONFIRM &&
      serviceReservationId
    ) {
      return (
        <Button
          variant="outlined"
          onClick={() => confirmServiceReservation(serviceReservationId)}
        >
          Conferma la prenotazione
        </Button>
      );
    }
    return <></>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    status,
    serviceReservationId,
    enableStatusChange,
    sendPushOnStatusChange,
  ]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
      {askCapturedAmount && (
        <>
          <Typography variant="caption">Importo da addebitare</Typography>
          <Input
            disabled={loading}
            type="number"
            defaultValue={serviceReservation.totalPrice}
            value={amountToCapture}
            onChange={(evt) => setAmountToCapture(Number(evt.target.value))}
          />
        </>
      )}
      <Chip style={{ backgroundColor, color }} label={label} />
      {changeStatusButton}
    </div>
  );
}
